import * as p from '@clack/prompts'
import type { BetterstartCommandRuntime, Cancellable } from '@cli-types/core-engine/commands.js'
import { ADD_COMMAND, CANCELLED, REMOVE_COMMAND } from '@cli-constants/commands.js'
import { cancelled } from './cancelled.js'
import { promptInstallableFamily } from './prompt-installable-family.js'

/**
 * `add` and `remove` take preset IDs by default and integration IDs only
 * behind `--integration`, and refuse a mix, so the family is chosen first.
 */
export async function promptInstallables(
  name: typeof ADD_COMMAND | typeof REMOVE_COMMAND,
  runtime: BetterstartCommandRuntime,
  cwd: string
): Promise<Cancellable<string[]>> {
  const removing = name === REMOVE_COMMAND
  const choices = await runtime.listInstallableChoices(cwd)
  const presets = choices.presets.filter((choice) => choice.installed === removing)
  const integrations = choices.integrations.filter((choice) => choice.installed === removing)

  if (presets.length === 0 && integrations.length === 0) {
    p.log.warn(removing ? 'Nothing installed to remove.' : 'Everything available is installed.')
    return CANCELLED
  }

  const family = await promptInstallableFamily(presets, integrations)
  if (cancelled(family)) {
    return CANCELLED
  }

  const items = await p.multiselect({
    message: removing ? 'Items to remove' : 'Items to install',
    options: (family === 'integration' ? integrations : presets).map((choice) => ({
      value: choice.id,
      label: choice.id,
      hint: choice.description
    }))
  })
  if (p.isCancel(items)) {
    return CANCELLED
  }

  return family === 'integration' ? ['--integration', ...items] : items
}
